import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { IconAction } from '@/components/ui/icon-action';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiError, type Study, useImagingApi, useImagingCapabilities } from './api';

export function DeleteStudy({ study }: { study: Study }) {
  const { base, org } = useImagingApi();
  const capabilities = useImagingCapabilities();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!capabilities.data?.permissions.some(p => ['intake_imaging', 'prepare_imaging'].includes(p))) return null;
  async function remove() {
    setBusy(true);
    try {
      await clientApi.delete(`${base}/studies/${study.id}`, { params: { revision: study.revision } });
      await queryClient.invalidateQueries({ queryKey: ['imaging', org] });
      setOpen(false);
      toast.success('Study deleted');
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  }
  return <>
    <IconAction icon={Trash2} variant="destructive" label={study.signed_document_id ? 'Signed study cannot be deleted' : 'Delete study'} disabled={!!study.signed_document_id} onClick={() => setOpen(true)} />
    <Dialog open={open} onOpenChange={value => { if (!busy) setOpen(value); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Delete study?</DialogTitle>
          <DialogDescription>This removes the study from active worklists and study access. Source files and audit history are retained; this does not permanently erase stored files or delete the patient.</DialogDescription>
        </DialogHeader>
        <p>{study.metadata.PatientName} · {study.metadata.PatientID} · {study.metadata.StudyDescription || study.description || 'Ultrasound'}</p>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="destructive" disabled={busy} onClick={() => void remove()}>{busy ? 'Deleting…' : 'Delete study'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
